import { useMemo } from "react";
import type { ComponentType } from "react";
import { useSearchParams } from "react-router-dom";

import PageHeader from "@/components/PageHeader";
import MemoryBrowserPage from "./MemoryBrowser";
import MemoryGraphPage from "./MemoryGraph";
import EpisodesPage from "./Episodes";

type MemoryTab = {
  id: string;
  label: string;
  description: string;
  component: ComponentType;
};

const MEMORY_TABS: MemoryTab[] = [
  {
    id: "browser",
    label: "Search",
    description: "Semantic + metadata search, editing, and promotions.",
    component: MemoryBrowserPage,
  },
  {
    id: "graph",
    label: "Graph",
    description: "Clusters, links, and graph health.",
    component: MemoryGraphPage,
  },
  {
    id: "episodes",
    label: "Episodes",
    description: "Browse episodic entries per agent.",
    component: EpisodesPage,
  },
];

export default function MemoryConsolePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") ?? MEMORY_TABS[0].id;

  const tabList = useMemo(() => MEMORY_TABS, []);
  const currentTab = tabList.find((tab) => tab.id === activeTab) ?? MEMORY_TABS[0];
  const TabComponent = currentTab.component;

  const handleTabChange = (nextTab: string) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("tab", nextTab);
    setSearchParams(nextParams, { replace: true });
  };

  return (
    <div className="aa-page-shell space-y-6">
      <PageHeader
        title="Agent History"
        description="Search and explore what your agents have learned and remembered."
      />

      <div className="flex flex-wrap items-center gap-2">
        {tabList.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => handleTabChange(tab.id)}
            className={`rounded-full border px-4 py-1.5 text-sm transition ${
              tab.id === currentTab.id
                ? "border-primary/80 bg-primary/15 text-foreground"
                : "border-border text-foreground/70 hover:text-foreground"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <p className="text-sm text-muted-foreground">{currentTab.description}</p>

      <div className="aa-glass-card rounded-2xl p-4">
        <TabComponent />
      </div>
    </div>
  );
}
